import { Check, ChevronDown, X } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@bookdental/ui";
import { cn } from "@/lib/cn";
import {
  DENTIST_STATUS_META,
  DENTIST_STATUS_ORDER,
  type DentistStatusBucket,
} from "./dentist-status";

type Props = {
  selecionados: DentistStatusBucket[];
  contagemPorStatus: Record<DentistStatusBucket, number>;
  total: number;
  onAlternar: (status: DentistStatusBucket) => void;
  onLimpar: () => void;
  /** Largura do botão — na barra do celular ele divide a linha com Ordenar e a visualização. */
  className?: string;
};

/**
 * Filtro de status no celular: um botão que abre a lista com marcação; os escolhidos viram pílulas
 * (`PilulasDeStatus`) logo abaixo da linha de filtros.
 *
 * A faixa rolável de chips resolvia o transbordo, mas escondia atrás de um gesto quais filtros
 * existiam, e só deixava escolher um por vez. Aqui a lista fica visível de uma vez e a escolha é
 * múltipla — nenhum selecionado significa todos, que é como o filtro já se comportava.
 *
 * Só é montado abaixo de `md`; no desktop os chips continuam iguais.
 */
export function ProsthesisStatusFilterMenu({
  selecionados,
  contagemPorStatus,
  total,
  onAlternar,
  onLimpar,
  className,
}: Props) {
  const quantos = selecionados.length;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            "inline-flex h-11 w-full items-center justify-between gap-2 rounded-lg border px-3 text-[13px] font-semibold transition-colors",
            className,
            quantos > 0
              ? "border-brand-200 bg-brand-50 text-brand-700 dark:border-primary/30 dark:bg-primary/15 dark:text-primary"
              : "border-zinc-200 bg-zinc-50 text-zinc-700 dark:border-border dark:bg-muted/40 dark:text-foreground",
          )}
        >
          <span>
            Status
            {quantos > 0 && (
              <span className="ml-1.5 font-bold">
                ({quantos})
              </span>
            )}
          </span>
          <ChevronDown className="h-4 w-4 shrink-0 opacity-60" />
        </button>
      </PopoverTrigger>

      <PopoverContent
        align="start"
        sideOffset={6}
        collisionPadding={12}
        className="w-[calc(100vw-24px)] max-w-[320px] border border-zinc-200 bg-white p-1 shadow-xl dark:border-border dark:bg-card"
      >
        <button
          type="button"
          onClick={onLimpar}
          className="flex h-11 w-full items-center justify-between gap-2 rounded-md px-2.5 text-[13px] font-medium text-zinc-700 transition-colors hover:bg-zinc-50 dark:text-foreground dark:hover:bg-muted"
        >
          <span className="flex items-center gap-2">
            <span
              className={cn(
                "flex h-4 w-4 shrink-0 items-center justify-center rounded border",
                quantos === 0
                  ? "border-brand-600 bg-brand-600 text-white dark:border-primary dark:bg-primary"
                  : "border-zinc-300 dark:border-border",
              )}
            >
              {quantos === 0 && <Check className="h-3 w-3" strokeWidth={3} />}
            </span>
            Todos
          </span>
          <span className="text-[11px] tabular-nums text-zinc-500 dark:text-muted-foreground">
            {total}
          </span>
        </button>

        {DENTIST_STATUS_ORDER.map((s) => {
          const marcado = selecionados.includes(s);
          return (
            <button
              key={s}
              type="button"
              onClick={() => onAlternar(s)}
              className="flex h-11 w-full items-center justify-between gap-2 rounded-md px-2.5 text-[13px] font-medium text-zinc-700 transition-colors hover:bg-zinc-50 dark:text-foreground dark:hover:bg-muted"
            >
              <span className="flex min-w-0 items-center gap-2">
                <span
                  className={cn(
                    "flex h-4 w-4 shrink-0 items-center justify-center rounded border",
                    marcado
                      ? "border-brand-600 bg-brand-600 text-white dark:border-primary dark:bg-primary"
                      : "border-zinc-300 dark:border-border",
                  )}
                >
                  {marcado && <Check className="h-3 w-3" strokeWidth={3} />}
                </span>
                <span className="truncate">{DENTIST_STATUS_META[s].label}</span>
              </span>
              <span className="shrink-0 text-[11px] tabular-nums text-zinc-500 dark:text-muted-foreground">
                {contagemPorStatus[s]}
              </span>
            </button>
          );
        })}
      </PopoverContent>
    </Popover>
  );
}

/** Os status escolhidos, como pílulas que tiram o filtro ao tocar. */
export function PilulasDeStatus({
  selecionados,
  onAlternar,
}: {
  selecionados: DentistStatusBucket[];
  onAlternar: (status: DentistStatusBucket) => void;
}) {
  if (selecionados.length === 0) return null;
  return (
    <ul className="flex flex-wrap gap-1.5">
      {selecionados.map((s) => (
        <li key={s}>
          <button
            type="button"
            onClick={() => onAlternar(s)}
            aria-label={`Remover filtro ${DENTIST_STATUS_META[s].label}`}
            className="inline-flex h-8 items-center gap-1 rounded-full px-2.5 text-[11.5px] font-semibold"
            style={{
              backgroundColor: DENTIST_STATUS_META[s].bg,
              color: DENTIST_STATUS_META[s].color,
            }}
          >
            {DENTIST_STATUS_META[s].label}
            <X className="h-3.5 w-3.5" />
          </button>
        </li>
      ))}
    </ul>
  );
}
